import { useCallback, useEffect, useState } from "react";
import { Button, Flex, Image, Modal, Table, TablePaginationConfig } from "antd";
import styled from "styled-components";
import { TParamsUser } from "services/UserManagement";
import { User } from "services/auth";
import { SorterResult } from "antd/es/table/interface";
import moment from "moment";
import {
  forceDeletePostApi,
  getPostReportsApi,
  putCommentReportsApi,
} from "services/user-report";
import { API_URL } from "configAxios";

const initFilter = {
  page: 1,
  limit: 10,
  total: 0,
};

type TPost = {
  title?: string;
  caption?: string;
  files: { url: string; id: number }[];
};

type Report = {
  createdAt: string;
  description: string;
  id: number;
  photo: string;
  reason: string;
  reported: User;
  user: User;
  post: TPost;
};

const PostReport = () => {
  const [data, setData] = useState<Report[]>([]);
  const [params, setParams] = useState<TParamsUser>(initFilter);
  const [isLoading, setIsLoading] = useState(false);
  const [videoModal, setVideoModal] = useState<{
    visible: boolean;
    url: string | null;
  }>({ visible: false, url: null });

  const fetchData = useCallback(async (params?: TParamsUser) => {
    setIsLoading(true);
    try {
      const { data } = await getPostReportsApi(params);
      setData(data.reports);
      setParams((pre) => ({
        ...pre,
        total: data.total,
        page: params?.page ?? pre.page,
      }));
    } catch (error: any) {
      Modal.error({
        content: error.response?.data.message || "Fetch data failed",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDeletePost = useCallback(
    (recordId: number, postId: number) => {
      Modal.confirm({
        title: "Delete Post",
        content: "Are you sure you want to delete this post?",
        okText: "Confirm",
        cancelText: "Cancel",
        onOk: async () => {
          await forceDeletePostApi(postId);
          await putCommentReportsApi(recordId, true);
          fetchData();
        },
      });
    },
    [fetchData]
  );

  const handleChangeTable = useCallback(
    (
      { current, pageSize }: TablePaginationConfig,
      _: any,
      { order, field }: SorterResult
    ) => {
      if (!current || !pageSize) return;
      fetchData({
        ...params,
        page: current!,
        limit: pageSize!,
        order: order ? (order === "ascend" ? "asc" : "desc") : "",
        sortBy: (field as string) || "",
      });
    },
    [fetchData, params]
  );

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: ({ firstName, lastName }: User) => (
        <p>{`${firstName || ""} ${lastName || ""}`}</p>
      ),
    },
    {
      title: "Post title",
      dataIndex: "post",
      key: "post_title",
      render: (post: TPost) => post?.title || "--",
    },
    {
      title: "Post caption",
      dataIndex: "post",
      key: "post_caption",
      render: (post: TPost) => post?.caption || "--",
    },
    {
      title: "Post medias",
      dataIndex: "post",
      key: "post_medias",
      render: (post: TPost) => {
        const { files = [] } = post || {};
        if (!files || files.length === 0) return "--";
        return (
          <div style={{ display: "flex", gap: 8 }}>
            {files.map((e) => {
              const isVideo = e.url.match(/\.(mp4|webm|ogg)$/i) !== null;
              if (isVideo) {
                return (
                  <video
                    key={e.id}
                    width={50}
                    height={50}
                    controls={false}
                    style={{
                      objectFit: "cover",
                      cursor: "pointer",
                      background: "black",
                    }}
                    onClick={() => {
                      setVideoModal({
                        visible: true,
                        url: e.url.startsWith("http")
                          ? e.url
                          : `${API_URL}/api/uploads/${e.url}`,
                      });
                    }}
                    autoPlay={false}
                    playsInline={false}
                  >
                    <source
                      src={
                        e.url.startsWith("http")
                          ? e.url
                          : `${API_URL}/api/uploads/${e.url}`
                      }
                    />
                    Your browser does not support the video tag.
                  </video>
                );
              } else {
                return (
                  <Image
                    key={e.id}
                    src={
                      e.url.startsWith("http")
                        ? e.url
                        : `${API_URL}/api/uploads/${e.url}`
                    }
                    alt="media"
                    style={{ width: 50, height: 50, objectFit: "cover" }}
                  />
                );
              }
            })}
          </div>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Photo",
      dataIndex: "photo",
      key: "photo",
      render: (text: string) => (
        <Image
          src={`${API_URL}/api/uploads/${text}`}
          alt="avatar user"
          style={{ width: 50, height: 50 }}
        />
      ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (text: string) => <p>{moment(text).format("MM/DD/YYYY")}</p>,
    },
    {
      title: "",
      width: 130,
      dataIndex: "action",
      key: "creaactiontedAt",
      render: (_: any, r: any) => {
        if (!r.post) return null;
        return (
          <Button danger onClick={() => onDeletePost(r.id, r.post.id)}>
            Delete Post
          </Button>
        );
      },
    },
  ];

  return (
    <WrapperUserManagement>
      <h1>User Report</h1>
      <Table<Report>
        loading={isLoading}
        dataSource={data}
        onChange={handleChangeTable as any}
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: params.total,
          showSizeChanger: false,
        }}
        columns={columns}
      />
      <Modal
        open={videoModal.visible}
        onCancel={() => setVideoModal({ visible: false, url: null })}
        footer={null}
        width={800}
        centered
        destroyOnClose
      >
        {videoModal.url && (
          <video
            width="100%"
            height="440"
            controls
            autoPlay
            style={{ background: "#000" }}
          >
            <source src={videoModal.url} />
            Your browser does not support the video tag.
          </video>
        )}
      </Modal>
    </WrapperUserManagement>
  );
};

export default PostReport;

const WrapperUserManagement = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 20px;
  &.ant-pagination {
    margin: 8px 0px !important;
  }
  > h1 {
    margin: 0;
  }
`;
